<template>
  <div
    v-if="FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO"
    class="mb-12 flex flex-wrap gap-8"
  >
    <div>Content cards: {{ contentCards.length }}</div>
    <div>cards: {{ cards.length }}</div>

    <div>isRequestingContentCards (Braze): {{ isRequestingContentCards }}</div>
    <div>brazeUnavailable: {{ brazeUnavailable }}</div>
    <div>currentContentCard.props: {{ currentContentCard?.props }}</div>
    <div>currentContentCard.tag: {{ currentContentCard?.tag }}</div>
    <div>activeTabIndex: {{ activeTabIndex }}</div>
    <div>fallbackError: {{ fallbackError }}</div>
    <div>currentCardUsesFallback {{ currentCardUsesFallback }}</div>
    <div>fallbackCards: {{ fallbackCards }}</div>
  </div>

  <Background
    :background
    :class="{
      'p-16 md:p-24 rounded-lg': !!background,
    }"
    container-class="!items-start"
  >
    <RevSkeleton
      v-if="forceSingleContent && isRequestingContentCards"
      class="mb-16"
      height="32px"
      shape="rectangle"
      width="206px"
    />
    <BlockTitle v-else :title="mainTitle" />

    <div
      v-if="!forceSingleContent"
      ref="tabs-scroller"
      class="mb-20 mt-4 flex grow flex-col justify-between overflow-auto scrollbar-none max-w-full"
      :class="maskClass"
    >
      <div
        :aria-label="title || ''"
        class="flex w-max items-start gap-8 overflow-x-auto overflow-y-hidden scrollbar-none max-w-full"
        role="tablist"
      >
        <template v-if="isRequestingContentCards">
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
          <RevSkeleton height="40px" shape="rectangle" width="130px" />
        </template>

        <RevChip
          v-for="(tab, tabIndex) in displayedContentCardTabs"
          v-else
          :id="`tab-${tabIndex}`"
          :key="tab.id"
          ref="tabItem"
          aria-controls="content-card-panel"
          :aria-selected="activeTabIndex === tabIndex"
          class="shrink-0"
          :data-test="`tab-${tabIndex}`"
          :end-icon="tab.icon"
          :label="tab.title"
          :model-value="activeTabIndex === tabIndex"
          role="tab"
          :tabindex="activeTabIndex === tabIndex ? 0 : -1"
          @click="() => onTabClick(tabIndex)"
          @keydown.left.prevent="() => goToPreviousTab()"
          @keydown.right.prevent="() => goToNextTab()"
        />
      </div>
    </div>

    <div
      id="content-card-panel"
      :aria-labelledby="
        forceSingleContent ? undefined : `tab-${activeTabIndex}`
      "
      class="h-[408px] md:h-[394px] w-full"
      :role="forceSingleContent ? undefined : 'tabpanel'"
    >
      <CardCollectionSkeleton
        v-if="isRequestingContentCards"
        layout="carousel"
      />
      <component
        :is="currentContentCard?.tag || null"
        v-show="!isRequestingContentCards"
        v-bind="currentContentCard?.props"
        @error="handleCardError"
        @loaded="handleCardLoaded"
      />

      <ErrorState v-if="fallbackError" @retry="handleRetry" />
    </div>
  </Background>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { RevChip } from '@ds/components/Chip'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import Background from '../../shared-components/Background/Background.vue'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'
import CardCollectionSkeleton from '../../shared-components/CardCollectionSkeleton/CardCollectionSkeleton.vue'
import { ON_RETRY_INJECTION_KEY } from '../RefinedProducts/retry/on-retry.injection-key'
import type { RetryFunction } from '../RefinedProducts/retry/retry-function'

import {
  transformContentCards,
  transformFallbackBlocks,
} from './PersonalizedFeed.utils'
import ErrorState from './components/ErrorState.vue'
import { useBrazeCards } from './composables/useBrazeCards'
import { useActiveTabIndex } from './composables/useTabIndex'

export type PersonalizedFeedBlockProps = PersonalizedFeedApiProps & BlockProps

const {
  feed,
  fallback,
  blockPosition,
  title,
  forceSingleContent,
  productQuery,
} = defineProps<PersonalizedFeedBlockProps>()

const { FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO = false } =
  useRuntimeConfig().public

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error'])

// const isLoading = ref(true)
const fallbackError = ref(false)

const tabItem = useTemplateRef('tabItem')
const scrollerRef = useTemplateRef<HTMLDivElement>('tabs-scroller')
const { maskClass } = useScrollMaskClass(scrollerRef)

const brazeUnavailable = ref(false)

const { cards, fetchCards, isRequestingContentCards, waitForBrazeToBeReady } =
  useBrazeCards()

const contentCards = computed(() =>
  transformContentCards({
    cards: cards.value,
    feed,
    productQuery,
  }),
)

const fallbackCards = computed(() =>
  transformFallbackBlocks({
    fallback,
    blockPosition,
    title,
  }),
)

const {
  activeTabIndex,
  displayFallback: currentCardUsesFallback,
  setActiveTabIndex,
  goToPreviousTab,
  goToNextTab,
} = useActiveTabIndex(contentCards)

const hydrationCompleted = ref(false)

const currentContentCard = computed(() => {
  if (!hydrationCompleted.value) {
    return null
  }

  if (brazeUnavailable.value) {
    return fallbackCards.value[activeTabIndex.value]
  }

  if (contentCards.value.length === 0) {
    return fallbackCards.value[activeTabIndex.value]
  }

  if (currentCardUsesFallback.value) {
    return fallbackCards.value[activeTabIndex.value]
  }

  return contentCards.value[activeTabIndex.value]
})

const displayedContentCardTabs = computed(() => {
  if (brazeUnavailable.value) {
    return fallbackCards.value
  }

  if (contentCards.value.length === 0) {
    return fallbackCards.value
  }

  return contentCards.value
})

const mainTitle = computed(() => {
  // if forceSingleContent, we display the title of the first card (if it exists)
  if (forceSingleContent) {
    return displayedContentCardTabs.value?.[0]?.title || title
  }

  return title
})

const { isBrazeUserConsentGiven } = useDataConsent()

function setInitialState(canUseBraze: boolean) {
  activeTabIndex.value = 0
  if (canUseBraze) {
    fetchCards()
    brazeUnavailable.value = false
    fallbackError.value = false
  } else {
    brazeUnavailable.value = true
    fallbackError.value = false
  }
}

onMounted(async () => {
  hydrationCompleted.value = true

  const { isEnabled } = await waitForBrazeToBeReady()
  setInitialState(isEnabled)
})

watch(
  () => isBrazeUserConsentGiven.value,
  (consent) => {
    setInitialState(consent)
  },
)

let retryFunctions = Array<RetryFunction>()

function useRegisterRetryFn(fn: RetryFunction) {
  retryFunctions.push(fn)

  onUnmounted(() => {
    retryFunctions = retryFunctions.filter((f) => f !== fn)
  })
}

provide(ON_RETRY_INJECTION_KEY, useRegisterRetryFn)

function onTabClick(i: number) {
  fallbackError.value = false

  setActiveTabIndex(i)
  requestAnimationFrame(() => {
    const targetTab = tabItem.value?.find(
      (el) => el?.$attrs.id === `tab-${i}`,
    )?.$el
    if (targetTab) {
      targetTab.focus()
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  })
}

function handleCardLoaded() {
  fallbackError.value = false
}

function handleCardError() {
  handleCardLoaded()

  const usingFallback = brazeUnavailable.value || currentCardUsesFallback.value

  // If an error has been generated while we had a fallback, we display the error state
  if (usingFallback) {
    fallbackError.value = true

    // If there is no fallback for the current tab, we display the error state
  } else if (!fallbackCards.value[activeTabIndex.value]) {
    fallbackError.value = true
  }
  // otherwise we try to display the fallback
  else {
    currentCardUsesFallback.value = true
  }
}

function handleRetry() {
  fallbackError.value = false
  retryFunctions.forEach((fn) => fn?.())
}
</script>
